import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Bell, Sparkles, Camera, FileText, Volume2, VolumeX } from 'lucide-react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import PhoneMockup from '../components/PhoneMockup';
import WhatsAppDemo from '../components/WhatsAppDemo';
import PricingSection from '../components/PricingSection';
import LandingHeader from '../components/LandingHeader';
import SolutionsSection from '../components/SolutionsSection';
import Testimonials from '../components/Testimonials';
import TaxGuidesSection from '../components/TaxGuidesSection';
import SiteFooter from '../components/SiteFooter';
import { useCountry } from '../contexts/CountryContext';

const HeroShowcaseVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 0.5) {
          // Scrolled mostly out of view: mute (and pause when fully gone)
          el.muted = true;
          setMuted(true);
          if (entry.intersectionRatio === 0) el.pause();
        } else if (el.paused) {
          el.play().catch(() => {});
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    setMuted(next);
    if (!next) el.play().catch(() => {});
  };

  return (
    <div className="mt-8 max-w-4xl mx-auto" data-testid="hero-video-wrap">
      <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[#064E3B]/10 bg-black">
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="w-full h-auto block"
          src="/videos/hero.mp4"
          poster="/videos/hero-poster.jpg"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
        <button
          type="button"
          onClick={toggleSound}
          data-testid="hero-video-sound-toggle"
          aria-label={muted ? 'Unmute video' : 'Mute video'}
          className="absolute bottom-4 right-4 inline-flex items-center justify-center h-11 w-11 rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/80 transition-colors focus:outline-none focus:ring-2 focus:ring-[#84CC16]"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-3">Tap the speaker to hear it. Sound mutes automatically as you scroll on.</p>
    </div>
  );
};

const NUDGES = [
  { title: 'Quarterly tax reminder', msg: '“Heads up — your Q2 estimated tax (~$1,240) is due Jun 15. Want the breakdown?”' },
  { title: 'Bill due alert', msg: '“Your $89 software subscription is due tomorrow — reply ‘paid’ and I’ll log it.”' },
  { title: 'Weekly money recap', msg: '“This week: +$1,800 in, $420 out. You’ve set aside $540 for tax. 👍”' },
  { title: 'Deduction spotted', msg: '“That $60 counts as a software expense — categorized and ~$18 saved in tax.”' },
];

export const Landing = () => {
  const navigate = useNavigate();
  const { content: c } = useCountry();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#E0F2FE] to-[#D1FAE5] relative">
      <LandingHeader />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#84CC16]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#064E3B]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#84CC16]/5 to-[#064E3B]/5 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Logo & Header */}
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#84CC16] mb-2" data-testid="hero-tagline">
              Text It. Snap It. Books Ready.
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] mb-3 tracking-tight" data-testid="hero-h1">
              Your bookkeeping app, now on <span className="text-[#84CC16]">WhatsApp.</span>
            </h1>
            {/* Kept for SEO & accessibility, visually hidden per design */}
            <p className="sr-only" data-testid="hero-subheadline">
              Send receipts and expenses on WhatsApp. MoneyFeed sorts them and delivers clean, categorized records to QuickBooks, Xero, or Excel — no new software to learn.
            </p>

            <HeroShowcaseVideo />

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10 mb-7">
              <Button
                data-testid="get-started-btn"
                onClick={() => navigate('/auth')}
                className="w-full sm:w-[220px] bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold text-base px-8 py-6 rounded-full transition-colors group"
              >
                Get Started Free
                <ArrowRight className="h-5 w-5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                variant="outline"
                data-testid="see-how-it-works-btn"
                onClick={() => navigate('/demo')}
                className="w-full sm:w-[220px] bg-white border-[#064E3B]/25 text-[#064E3B] hover:bg-[#064E3B]/5 font-bold text-base px-8 py-6 rounded-full transition-colors"
              >
                See How It Works
              </Button>
            </div>

            {/* App store badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6" data-testid="store-badges">
              <a
                href="https://apps.apple.com/app/moneyfeed-ai/id6788915539"
                target="_blank" rel="noopener noreferrer"
                aria-label="Download MoneyFeed on the App Store"
                data-testid="app-store-badge"
                className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-5 py-2.5 hover:bg-gray-800 transition-colors"
              >
                <FaApple className="h-7 w-7" />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] opacity-80">Download on the</span>
                  <span className="block text-lg font-semibold -mt-0.5">App Store</span>
                </span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.moneyfeed.app"
                target="_blank" rel="noopener noreferrer"
                aria-label="Get MoneyFeed on Google Play"
                data-testid="play-store-badge"
                className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-5 py-2.5 hover:bg-gray-800 transition-colors"
              >
                <FaGooglePlay className="h-6 w-6" />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] opacity-80">GET IT ON</span>
                  <span className="block text-lg font-semibold -mt-0.5">Google Play</span>
                </span>
              </a>
            </div>

            {/* Formats + accountant link */}
            <div className="mb-6" data-testid="hero-formats">
              <p className="text-sm font-semibold text-[#064E3B]">
                CPA-Ready Reports · Works with QuickBooks &amp; Xero · CSV · Excel · PDF
              </p>
              <a href="/partners/accountants" className="inline-block mt-2 text-sm text-[#064E3B]/70 underline hover:text-[#84CC16] transition-colors" data-testid="hero-accountant-link">
                Are you an accountant? Become a Partner
              </a>
            </div>

            {/* Three concise benefits */}
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="hero-benefits">
              <span className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#064E3B]">
                <Camera className="h-5 w-5 text-[#84CC16]" /> Capture Receipts
              </span>
              <span className="hidden sm:inline text-gray-300">·</span>
              <span className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#064E3B]">
                <Sparkles className="h-5 w-5 text-[#84CC16]" /> AI Categorization
              </span>
              <span className="hidden sm:inline text-gray-300">·</span>
              <span className="inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#064E3B]">
                <FileText className="h-5 w-5 text-[#84CC16]" /> Accountant-Ready Reports
              </span>
            </div>
          </div>

          {/* See it in action — live animated WhatsApp demo */}
          <div className="mb-16" data-testid="demo-section">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#D1FAE5] rounded-full mb-4">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#84CC16] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#84CC16]"></span>
                </span>
                <span className="text-sm font-bold text-[#064E3B] uppercase tracking-wider">See it in action</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">Just text. We do the rest.</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">Watch a real MoneyFeed chat — snap a receipt, log an expense, track mileage and record income in seconds.</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
              <WhatsAppDemo />
              <div className="space-y-6">
                <DemoPoint num="1" title="Snap a receipt or just text" text="📷 Photograph a receipt, send a PDF invoice, or type “spent 45 on gas” — no forms, no apps. Right inside WhatsApp. Scans capture the invoice number and every line item, every receipt gets its own MF number (MF-####), and you can review the items in the chat and edit them from the review link." />
                <DemoPoint num="2" title="AI logs & categorizes instantly" text={c.demoCategory} />
                <DemoPoint num="3" title="Mileage & income too" text={c.demoMileage} />
                <div className="pt-2">
                  <Button
                    onClick={() => navigate('/auth')}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] hover:from-[#65A30D] hover:to-[#4d7c0f] text-white font-bold px-7 py-6 rounded-full text-lg shadow-lg"
                    data-testid="demo-cta-btn"
                  >
                    Try it free <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Proactive nudges */}
          <div className="mb-16" data-testid="nudges-section">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#D1FAE5] rounded-full mb-4">
                <Bell className="h-4 w-4 text-[#064E3B]" />
                <span className="text-sm font-bold text-[#064E3B] uppercase tracking-wider">Never miss a deadline</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">MoneyFeed pings you first</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">Like a bookkeeping assistant checking in, MoneyFeed messages you before taxes are due, when a bill's coming up, and with a weekly money recap — right on WhatsApp.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {NUDGES.map((n, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-md p-5 border border-gray-100 hover:-translate-y-1 transition-transform duration-200" data-testid={`nudge-card-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-8 w-8 rounded-full bg-[#84CC16] flex items-center justify-center text-[#064E3B] font-bold text-sm flex-shrink-0">M</span>
                    <span className="text-xs font-bold text-[#064E3B]">{n.title}</span>
                  </div>
                  <div className="bg-[#DCF8C6] rounded-2xl rounded-tl-sm px-3 py-2.5 text-sm text-gray-800 leading-snug">
                    {n.msg}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solutions — showcases every product page (anchor target for header "Features") */}
          <div id="features" className="scroll-mt-24">
            <SolutionsSection />
          </div>

          {/* Tax guides by country & US state */}
          <TaxGuidesSection />

          {/* Testimonials — real, approved user reviews (renders only when present) */}
          <Testimonials />

          {/* Features Grid + Regions removed (duplicated Solutions & Tax Guides) */}

          {/* Why MoneyFeed vs accounting software */}
          <section className="hidden md:block mb-16" data-testid="vs-accounting-section">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">Already on QuickBooks or Xero? Keep it.</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">MoneyFeed captures receipts and expenses on WhatsApp, sorts them, and hands your software — and your CPA — a clean, ready file.</p>
            </div>
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
              <div className="grid grid-cols-3 text-sm md:text-base">
                <div className="p-4 md:p-5 font-semibold text-gray-500"> </div>
                <div className="p-4 md:p-5 font-bold text-[#064E3B] text-center bg-[#84CC16]/10 border-x border-slate-200">MoneyFeed</div>
                <div className="p-4 md:p-5 font-semibold text-gray-500 text-center">Accounting software</div>
                {[
                  ['Getting started', 'Just text on WhatsApp', 'Set up software + learn it'],
                  ['Who it\u2019s for', 'Owners, freelancers, staff', 'Bookkeepers & accountants'],
                  ['Focus', 'Getting you filed', 'Full ledgers & reports'],
                  ['Tax deadlines', 'Proactive reminders', 'You track them'],
                  ['Filing packet', 'Schedule C / SE / 1040-ES ready', 'Export, then rebuild for tax'],
                  ['Languages on chat', 'English · Spanish · Arabic', 'App UI only'],
                ].map(([label, a, b], i) => (
                  <React.Fragment key={i}>
                    <div className="p-4 md:p-5 border-t border-slate-100 font-medium text-gray-700">{label}</div>
                    <div className="p-4 md:p-5 border-t border-slate-100 border-x border-slate-200 bg-[#84CC16]/5 text-center text-[#064E3B] font-semibold">{a}</div>
                    <div className="p-4 md:p-5 border-t border-slate-100 text-center text-gray-500">{b}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4 max-w-2xl mx-auto">Already on QuickBooks? Keep it — MoneyFeed can still handle your deadlines, filing packet &amp; <Link to="/for-accountants" className="font-semibold text-[#65A30D] hover:text-[#064E3B] underline underline-offset-2" data-testid="vs-accounting-for-accountants-link">CPA hand-off</Link>.</p>
          </section>


          {/* Pricing (public, SEO) — hidden inside the iOS app (Apple 3.1.1) */}
          <PricingSection />

          {/* How to Use section removed (duplicated the live Demo section examples) */}

          {/* Coming Soon - SMS Banking (compact teaser) */}
          <div className="hidden md:flex items-center justify-center gap-3 mb-16" data-testid="sms-coming-soon">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16] rounded-full flex-shrink-0">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
              </span>
              <span className="text-black font-bold text-xs uppercase tracking-wider">Coming Soon</span>
            </span>
            <p className="text-sm font-semibold text-[#064E3B]">
              SMS Banking Integration — auto-import expenses straight from your bank's text messages.
            </p>
          </div>

          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10 text-sm" aria-label="Learn more about MoneyFeed" data-testid="home-learn-more-links">
            <Link to="/features" className="text-[#064E3B] underline underline-offset-2 hover:text-[#65A30D]" data-testid="home-link-features">MoneyFeed Features</Link>
            <Link to="/security" className="text-[#064E3B] underline underline-offset-2 hover:text-[#65A30D]" data-testid="home-link-security">Security &amp; Data Protection</Link>
            <Link to="/supported-countries" className="text-[#064E3B] underline underline-offset-2 hover:text-[#65A30D]" data-testid="home-link-supported-countries">Supported Countries</Link>
            <Link to="/tax-support" className="text-[#064E3B] underline underline-offset-2 hover:text-[#65A30D]" data-testid="home-link-tax-support">Tax Support by Country</Link>
          </nav>

          {/* (Pricing section moved earlier for SEO) */}
        </div>
      </div>

      {/* Footer - Meta/WhatsApp Requirements */}
      <SiteFooter />
    </div>
  );
};

const DemoPoint = ({ num, title, text }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-[#064E3B] text-[#84CC16] font-bold flex items-center justify-center">{num}</div>
    <div>
      <h4 className="font-bold text-[#064E3B] text-lg">{title}</h4>
      <p className="text-gray-600 text-sm mt-0.5">{text}</p>
    </div>
  </div>
);

export default Landing;